import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportContext } from '../../context/SupportContext';
import { useAuth } from '../../context/AuthContext';
import { getEnterAgencyPath, getEnterAgencyPathFromSupportContext } from '../../utils/agencyType';
import axios from 'axios';
import {
  ChevronDown,
  Building2,
  User,
  Shield,
  Eye,
  ArrowLeft,
  Clock,
  Star,
  StarOff,
  LogIn,
  Search,
  Loader2,
  Check,
  X,
} from 'lucide-react';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Context Switcher - Phase 9
 * 
 * Top sidebar button that shows current context and opens a
 * dropdown/popover for switching between contexts.
 */
const ContextSwitcher = ({ variant = 'platform' }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { 
    supportContext, 
    isInSupportMode, 
    enterAgencyContext, 
    enterClientSupport, 
    exitSupportMode,
    isPlatformOwner,
  } = useSupportContext();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ agencies: [], users: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [recentContexts, setRecentContexts] = useState([]);
  const [pinnedContexts, setPinnedContexts] = useState([]);
  const [activeTab, setActiveTab] = useState('recent');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load recent/pinned from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentContexts');
    const pinned = localStorage.getItem('pinnedContexts');
    if (stored) {
      try { setRecentContexts(JSON.parse(stored)); } catch (e) {}
    }
    if (pinned) {
      try { setPinnedContexts(JSON.parse(pinned)); } catch (e) {}
    }
  }, []);

  // Save recent context
  const saveRecentContext = useCallback((context) => {
    setRecentContexts(prev => {
      const filtered = prev.filter(c => c.id !== context.id);
      const updated = [context, ...filtered].slice(0, 5);
      localStorage.setItem('recentContexts', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Toggle pinned context
  const togglePinned = useCallback((context) => {
    setPinnedContexts(prev => {
      const exists = prev.find(c => c.id === context.id);
      let updated;
      if (exists) {
        updated = prev.filter(c => c.id !== context.id);
      } else {
        updated = [context, ...prev].slice(0, 5);
      }
      localStorage.setItem('pinnedContexts', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isPinned = useCallback((id) => {
    return pinnedContexts.some(c => c.id === id);
  }, [pinnedContexts]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Search API
  const performSearch = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults({ agencies: [], users: [] });
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/owner-console/search`, {
        params: { q: query, limit: 5 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, [token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) performSearch(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Handle agency switch
  const handleAgencySwitch = async (agency) => {
    try {
      const result = await enterAgencyContext(agency.agency_id || agency.id);
      saveRecentContext({ 
        id: agency.agency_id || agency.id, 
        name: agency.name, 
        type: 'agency',
        agency_type: agency.agency_type || agency.type || agency.platform_type,
      });
      navigate(
        getEnterAgencyPathFromSupportContext(result, agency)
        || getEnterAgencyPath(agency)
      );
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch agency:', error);
    }
  };

  // Handle client support
  const handleClientSupport = async (user) => {
    try {
      const result = await enterClientSupport(user.user_id || user.id);
      saveRecentContext({ 
        id: user.user_id || user.id, 
        name: user.name || user.email, 
        type: 'client',
        role: user.role,
      });
      if (result.effective_portal_type === 'managed_client_portal') {
        navigate('/client/dashboard');
      } else {
        navigate('/dashboard');
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to enter client support:', error);
    }
  };

  // Handle return to platform
  const handleReturnToPlatform = async () => {
    await exitSupportMode();
    navigate('/platform/dashboard');
    setIsOpen(false);
  };

  // Get current context display - UNIFIED FundElite brand colors
  const getCurrentContextDisplay = () => {
    if (!isInSupportMode) {
      return {
        icon: Shield,
        label: 'Platform Owner',
        sublabel: 'Administrator Access',
        color: 'from-emerald-500 to-teal-600', // Unified emerald
        textColor: 'text-emerald-400',
      };
    }

    // Client support view = effective_user_id differs from real_actor_user_id
    const isClientView = supportContext?.effective_user_id && 
                         supportContext?.effective_user_id !== supportContext?.real_actor_user_id;
    
    if (isClientView) {
      const portalType = supportContext.effective_portal_type;
      return {
        icon: Eye,
        label: supportContext.effective_user_name || 'Client',
        sublabel: portalType === 'managed_client_portal' ? 'Managed Client View' : 'Self-Serve View',
        color: 'from-emerald-500 to-teal-600', // Unified emerald
        textColor: 'text-emerald-400',
      };
    }

    return {
      icon: Building2,
      label: supportContext?.effective_agency_name || 'Agency',
      sublabel: 'Agency Context',
      color: 'from-emerald-500 to-teal-600', // Unified emerald
      textColor: 'text-emerald-400',
    };
  };

  const currentContext = getCurrentContextDisplay();
  const CurrentIcon = currentContext.icon;

  if (!isPlatformOwner) return null;

  return (
    <div className="relative" ref={dropdownRef} data-testid="context-switcher">
      {/* Trigger Button - Glassmorphism Style with Unified Emerald */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 rounded-xl transition-all group"
        style={{
          background: isOpen 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(20, 184, 166, 0.1) 100%)'
            : 'rgba(255, 255, 255, 0.03)',
          border: isOpen 
            ? '1px solid rgba(16, 185, 129, 0.3)'
            : '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(8px)',
        }}
        data-testid="context-switcher-trigger"
      >
        {/* Icon */}
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${currentContext.color} flex items-center justify-center text-white shadow-lg`}>
          <CurrentIcon size={18} />
        </div>
        
        {/* Label */}
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium text-white truncate">{currentContext.label}</div>
          <div className={`text-xs ${currentContext.textColor} truncate`}>{currentContext.sublabel}</div>
        </div>
        
        {/* Chevron */}
        <ChevronDown 
          size={16} 
          className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Panel - Premium Glassmorphism */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-50 shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.99) 100%)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)',
          }}
          data-testid="context-switcher-dropdown"
        >
          {/* Search Input */}
          <div className="p-3 border-b border-white/5">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <Search size={16} className="text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search agencies or clients..."
                className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
                data-testid="context-switcher-search"
              />
              {isSearching && <Loader2 size={14} className="text-amber-400 animate-spin" />}
            </div>
          </div>

          {/* Tabs */}
          {!searchQuery && (
            <div className="flex border-b border-white/5">
              <button
                onClick={() => setActiveTab('recent')}
                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'recent' 
                    ? 'text-white border-b-2 border-emerald-500' 
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                <Clock size={12} className="inline mr-1.5" />
                Recent
              </button>
              <button
                onClick={() => setActiveTab('pinned')}
                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'pinned' 
                    ? 'text-white border-b-2 border-emerald-500' 
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                <Star size={12} className="inline mr-1.5" />
                Pinned
              </button>
            </div>
          )}

          {/* Content */}
          <div className="max-h-[300px] overflow-y-auto overscroll-contain">
            {/* Return to Platform Option */}
            {isInSupportMode && (
              <div className="p-2 border-b border-white/5">
                <button
                  onClick={handleReturnToPlatform}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group"
                  data-testid="return-to-platform-btn"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
                    <ArrowLeft size={16} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-white">Return to Platform Owner</div>
                    <div className="text-xs text-slate-400">Exit current context</div>
                  </div>
                </button>
              </div>
            )}

            {/* Search Results */}
            {searchQuery && (
              <div className="p-2">
                {searchResults.agencies.length === 0 && searchResults.users.length === 0 && !isSearching && (
                  <div className="px-3 py-6 text-center text-slate-400 text-sm">
                    No results found
                  </div>
                )}

                {/* Agencies */}
                {searchResults.agencies.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase">Agencies</div>
                    {searchResults.agencies.map(agency => (
                      <div
                        key={agency.agency_id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
                        onClick={() => handleAgencySwitch(agency)}
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400">
                          <Building2 size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{agency.name}</div>
                          <div className="text-xs text-slate-400">{agency.user_count || 0} users</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePinned({ id: agency.agency_id, name: agency.name, type: 'agency' }); }}
                          className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                        >
                          {isPinned(agency.agency_id) ? (
                            <Star size={14} className="text-amber-400 fill-amber-400" />
                          ) : (
                            <StarOff size={14} className="text-slate-500" />
                          )}
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {/* Users */}
                {searchResults.users.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase mt-2">Clients</div>
                    {searchResults.users.filter(u => ['self_repair_customer', 'managed_client'].includes(u.role)).map(user => (
                      <div
                        key={user.user_id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
                        onClick={() => handleClientSupport(user)}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          user.role === 'managed_client' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          <User size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{user.name || user.email}</div>
                          <div className="text-xs text-slate-400 truncate">{user.email}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.role === 'managed_client'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {user.role === 'managed_client' ? 'Managed' : 'Self-Serve'}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Recent/Pinned Lists */}
            {!searchQuery && (
              <div className="p-2">
                {activeTab === 'recent' && (
                  <>
                    {recentContexts.length === 0 ? (
                      <div className="px-3 py-6 text-center text-slate-400 text-sm">
                        No recent contexts
                      </div>
                    ) : (
                      recentContexts.map(ctx => (
                        <div
                          key={ctx.id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
                          onClick={() => ctx.type === 'agency' ? handleAgencySwitch(ctx) : handleClientSupport(ctx)}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            ctx.type === 'agency' 
                              ? 'bg-amber-500/20 text-amber-400'
                              : ctx.role === 'managed_client'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {ctx.type === 'agency' ? <Building2 size={16} /> : <User size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">{ctx.name}</div>
                            <div className="text-xs text-slate-400">
                              {ctx.type === 'agency' ? 'Agency' : ctx.role?.replace('_', ' ')}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); togglePinned(ctx); }}
                            className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                          >
                            {isPinned(ctx.id) ? (
                              <Star size={14} className="text-amber-400 fill-amber-400" />
                            ) : (
                              <StarOff size={14} className="text-slate-500" />
                            )}
                          </button>
                        </div>
                      ))
                    )}
                  </>
                )}

                {activeTab === 'pinned' && (
                  <>
                    {pinnedContexts.length === 0 ? (
                      <div className="px-3 py-6 text-center text-slate-400 text-sm">
                        No pinned contexts. Star items to pin them.
                      </div>
                    ) : (
                      pinnedContexts.map(ctx => (
                        <div
                          key={ctx.id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
                          onClick={() => ctx.type === 'agency' ? handleAgencySwitch(ctx) : handleClientSupport(ctx)}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            ctx.type === 'agency' 
                              ? 'bg-amber-500/20 text-amber-400'
                              : ctx.role === 'managed_client'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {ctx.type === 'agency' ? <Building2 size={16} /> : <User size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">{ctx.name}</div>
                            <div className="text-xs text-slate-400">
                              {ctx.type === 'agency' ? 'Agency' : ctx.role?.replace('_', ' ')}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); togglePinned(ctx); }}
                            className="p-1.5 rounded hover:bg-white/10 transition-all"
                          >
                            <Star size={14} className="text-amber-400 fill-amber-400" />
                          </button>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2.5 border-t border-white/5 text-xs text-slate-500 flex items-center justify-between">
            <span>Use Cmd+K for quick search</span>
            <span className="text-amber-400">Phase 9</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextSwitcher;
